import PropTypes from 'prop-types';

/**
 * What changed while you were gone.
 *
 * A SMALL PILE OF GOOD THINGS, which is the phrase from docs/game-design.md and
 * also the whole brief. Time away creates opportunity and never damage — there
 * is nothing this panel can tell you that is bad news, because there is nothing
 * in the game that can go wrong while you are not in it. No streak was broken,
 * nothing lapsed, nothing died. If a future line in here can only be delivered
 * as a loss, the mechanic behind it is the thing to reconsider, not the wording.
 *
 * IT ONLY EXISTS WHEN THERE IS SOMETHING TO SAY. The server sends nothing on a
 * first visit, after a refresh, or when the world was quiet — so this component
 * has no empty state and needs none. A panel that appears every session to
 * report that nothing happened is a chore list, and people learn to dismiss it
 * without reading, which costs the one session where it mattered.
 *
 * `pointer-events-auto` FOR THE REASON ChatPanel AND DialoguePanel CARRY IT: the
 * HUD wrapper above is `pointer-events-none` so clicks reach the world, and a
 * panel that does not opt back in never receives any. Its absence is silent —
 * the dismiss button looks fine and does nothing, because the click lands on the
 * canvas underneath and walks your character instead.
 */

/** "the Bodega, in midtown" — the world is worth naming; you may not live there. */
const where = ({ place, mapName }) => (place ? `${place}, in ${mapName}` : mapName);

const CLOSURE_WORDS = {
  temporary: 'shut for now',
  permanent: 'shut for good',
  // Defensive only — timeAway.js deliberately never reports 'soon' as news.
  // Without it the `|| 'shut'` fallback below would read wrong if it ever did.
  soon: 'opening soon',
};

function WelcomeBack({ summary, onDismiss }) {
  if (!summary) return null;

  const { awayFor, neighbours, places } = summary;

  return (
    <div className="pointer-events-auto w-full max-w-sm rounded-lg border border-gray-700 bg-gray-900/95 p-3 text-sm">
      <div className="flex items-start justify-between gap-2">
        <p className="text-gray-300">
          You were away
          {' '}
          <span className="text-amber-400">{awayFor}</span>
          .
        </p>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded px-1 text-gray-500 hover:text-gray-200"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>

      {neighbours.shown.length > 0 && (
        <div className="mt-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">New neighbours</p>
          <ul className="mt-1 space-y-0.5 text-gray-200">
            {neighbours.shown.map((n) => (
              <li key={`${n.mapName}-${n.username}-${n.place}`}>
                {n.username}
                {' moved into '}
                {where(n)}
              </li>
            ))}
          </ul>
          {neighbours.more > 0 && (
            // Counted rather than hidden. A month away should read as a
            // postcard, but "and 12 others" is still true where silence is not.
            <p className="mt-0.5 text-xs text-gray-500">{`and ${neighbours.more} more`}</p>
          )}
        </div>
      )}

      {places.shown.length > 0 && (
        <div className="mt-2">
          <p className="text-xs uppercase tracking-wide text-gray-500">Around town</p>
          <ul className="mt-1 space-y-0.5 text-gray-200">
            {places.shown.map((p) => (
              <li key={`${p.mapName}-${p.place}`}>
                {where(p)}
                {' is '}
                {CLOSURE_WORDS[p.closure] || 'shut'}
              </li>
            ))}
          </ul>
          {places.more > 0 && (
            <p className="mt-0.5 text-xs text-gray-500">{`and ${places.more} more`}</p>
          )}
        </div>
      )}
    </div>
  );
}

WelcomeBack.propTypes = {
  /** Null renders nothing — the common case, and not an error. */
  summary: PropTypes.shape({
    awayFor: PropTypes.string.isRequired,
    neighbours: PropTypes.shape({
      shown: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      more: PropTypes.number,
    }).isRequired,
    places: PropTypes.shape({
      shown: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      more: PropTypes.number,
    }).isRequired,
  }),
  onDismiss: PropTypes.func.isRequired,
};

WelcomeBack.defaultProps = { summary: null };

export default WelcomeBack;
